import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { apiService, graphBuilderApi } from '../../services/api';
import { AuditDashboard } from '../explainability';
import BuildGraphMenu from './BuildGraphMenu';
import './BuildGraph.css';
import './ExplainabilityPage.css';

function AuditPage() {
  const { graphId } = useParams();

  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadEvaluations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.listExecutionInsights(graphId, 1, 200);
      if (res.success) {
        const mapped = (res.insights || []).map(ins => ({
          execution_id: ins.insight_id,
          trace_id: ins.trace_id || ins.insight_id,
          query: ins.query || '',
          decision: ins.decision || ins.natural_language_answer?.substring(0, 80) || '-',
          confidence: ins.score != null ? `${Math.round(ins.score * 100)}%` : '-',
          analyst: ins.created_by || '-',
          timestamp: ins.created_at,
          created_at: ins.created_at,
          status: ins.status || 'completed',
        }));
        setEvaluations(mapped);
      } else {
        setError(res.error || 'Failed to load evaluations');
      }
    } catch (err) {
      console.error('Failed to load evaluations:', err);
      setError('Failed to load evaluations');
    } finally {
      setLoading(false);
    }
  }, [graphId]);

  useEffect(() => {
    loadEvaluations();
  }, [loadEvaluations]);

  const handleVerifyIntegrity = async (traceId) => {
    try {
      const result = await graphBuilderApi.verifyIntegrity(traceId);
      return result;
    } catch (err) {
      console.error('Integrity verification failed:', err);
      return { valid: false, error: err.message };
    }
  };

  const handleExportCompliance = async (evalItem) => {
    try {
      const response = await apiService.getCompliancePackage(evalItem.execution_id);
      if (response) {
        const blob = new Blob([response], { type: 'application/zip' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `compliance-${evalItem.execution_id.slice(0, 8)}-${Date.now()}.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Compliance export failed:', err);
    }
  };

  const handleViewEvaluation = (evalItem) => {
    const insightParam = evalItem?.insight_id || evalItem?.execution_id || '';
    const explainUrl = `/user-home/graphs/build/${graphId}/build-tune/explainability${insightParam ? `?insight=${insightParam}` : ''}`;
    window.open(explainUrl, '_blank');
  };

  return (
    <div className="build-graph-page">
      <BuildGraphMenu graphId={graphId} />
      <div className="audit-page-header">
        <h2>Audit Dashboard</h2>
        <p className="audit-page-subtitle">
          Review evaluation history, verify decision integrity, and export compliance packages.
        </p>
      </div>

      {error && <div className="audit-page-error">{error}</div>}

      <AuditDashboard
        evaluations={evaluations}
        loading={loading}
        onExportCompliance={handleExportCompliance}
        onVerifyIntegrity={handleVerifyIntegrity}
        onViewEvaluation={handleViewEvaluation}
      />
    </div>
  );
}

export default AuditPage;
